import React, { useRef, useEffect, useState } from 'react';
import { Send, MessageCircle } from 'lucide-react';

export default function AgentChatArea({ agent, onSendMessage }) {
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [agent?.messages]);

  const handleSend = () => {
    if (input.trim()) {
      onSendMessage(input);
      setInput('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!agent) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#050506]">
        <div className="text-center">
          <MessageCircle size={48} className="text-white/20 mx-auto mb-4" />
          <p className="text-white/50">Select an agent to start chatting</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-[#050506]">
      {/* Chat Header */}
      <div className="px-6 py-4 border-b border-white/5 bg-[#050506]/85 backdrop-blur">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-white">{agent.name}</h3>
            <p className="text-xs text-white/50 mt-1">{agent.description}</p>
          </div>
          {agent.whatsappConnected && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-[11px] text-white/70">
              <MessageCircle size={12} className="text-green-400" />
              <span>Synced to WhatsApp</span>
            </div>
          )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4">
        {agent.messages && agent.messages.length > 0 ? (
          <>
            {agent.messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex gap-3 ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.sender === 'agent' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 grid place-items-center text-[10px]">
                    🤖
                  </div>
                )}

                <div
                  className={`max-w-xs px-4 py-2.5 rounded-lg ${
                    msg.sender === 'user'
                      ? 'bg-[#10b981] text-white rounded-br-none'
                      : 'bg-white/10 text-white/90 rounded-bl-none border border-white/5'
                  }`}
                >
                  <p className="text-sm leading-relaxed">{msg.content}</p>
                  <p
                    className={`text-xs mt-1 ${
                      msg.sender === 'user' ? 'text-white/70' : 'text-white/40'
                    }`}
                  >
                    {msg.timestamp}
                  </p>
                  {msg.channel && (
                    <p className="text-[10px] mt-1 italic text-white/50">via {msg.channel}</p>
                  )}
                </div>

                {msg.sender === 'user' && (
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 grid place-items-center text-[10px]">
                    U
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-white/50">
            <p>No messages yet. Start by asking the agent a question.</p>
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="px-6 py-4 border-t border-white/5 bg-[#050506]/85">
        <div className="flex gap-3">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask the agent something..."
            className="flex-1 px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/40 focus:outline-none focus:border-[#10b981] transition-colors"
          />
          <button
            onClick={handleSend}
            disabled={!input.trim()}
            className="px-4 py-2.5 bg-[#10b981] text-black rounded-lg hover:bg-[#0da76f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
